import { Alert, Box, Paper } from "@mui/material";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { FireServices } from "../../../../shared/services/api/fire/FireServices";
import { AlfaSegurosLogo } from "../../../fire/icons/AlfaSeguroLogo";
import { PortoSeguroLogo } from "../../../fire/icons/PortoSeguroLogo";
import { TokioMarineLogo } from "../../../fire/icons/TokioMarineLogo";
import { InsurerCardLabel } from "./components/InsurerCardLabel";
import { VerticalSubTabs } from "./components/VerticalSubTabs";
import { TokioMarineManagerForm } from "./tabs/TokioMarineManagerForm";

type ApiInsurer = {
	id: string;
	name: string;
	document: string;
	logomarca: string | null;
	status?: string;
};

const LogoStub: React.FC<{ height?: number }> = ({ height = 36 }) => <Box sx={{ width: height, height, borderRadius: "50%", bgcolor: "divider" }} />;

const logoRegistry: Record<string, React.ComponentType<any>> = {
	TokioMarineLogo,
	PortoSeguroLogo,
	AlfaSegurosLogo,
};

interface InsurerSettingsTabsProps {
	id: string;
}

export const InsurerSettingsTabs: React.FC<InsurerSettingsTabsProps> = ({ id }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [insurers, setInsurers] = useState<ApiInsurer[]>([]);
	const [enabled, setEnabled] = useState<Record<string, boolean>>({});
	const [activeTabId, setActiveTabId] = useState<string | null>(null);
	const handleToggle = (insurerId: string) => setEnabled((prev) => ({ ...prev, [insurerId]: !prev[insurerId] }));

	useEffect(() => {
		if (id !== "novo") {
			setIsLoading(true);
			FireServices.getAllCompanyById(id).then((result) => {
				if (result instanceof Error) {
					setIsLoading(false);
					return;
				}
				const list = (result.data ?? []) as ApiInsurer[];
				setInsurers(list);

				const init: Record<string, boolean> = {};
				for (const item of list) init[item.id] = item.status === "active";
				setEnabled(init);

				const firstActiveId = list.find((item) => item.status === "active")?.id;
				setActiveTabId(firstActiveId || null);

				setIsLoading(false);
			});
		}
	}, [id]);

	const items = useMemo(() => {
		return insurers.map((ins) => {
			const Logo = ins.logomarca && logoRegistry[ins.logomarca] ? logoRegistry[ins.logomarca] : LogoStub;
			const isOn = !!enabled[ins.id];
			const isTabActive = ins.id === activeTabId;
			const renderInsurerForm = () => {
				if (!isOn) {
					return <Alert severity="info">Ative a seguradora no painel à esquerda para configurar.</Alert>;
				}

				const normalizedKey = (ins.logomarca || ins.name || "").toLowerCase();

				if (normalizedKey.includes("tokio")) {
					return <TokioMarineManagerForm clientId={id} insurerId={ins.id} shouldLoad={isTabActive} />;
				}

				return (
					<Alert severity="warning" sx={{ mt: 1 }}>
						Ainda não há um formulário dedicado para <strong>{ins.name}</strong>. Em desenvolvimento.
					</Alert>
				);
			};

			return {
				id: ins.id,
				label: <InsurerCardLabel name={ins.name} enabled={isOn} onToggle={() => handleToggle(ins.id)} Logo={Logo} dense />,
				content: (
					<Paper variant="outlined" sx={{ p: 2 }}>
						{renderInsurerForm()}
					</Paper>
				),
			};
		});
	}, [insurers, enabled, id, activeTabId]);

	return (
		<>
			<VerticalSubTabs paper height={"auto"} leftWidth={300} dense items={items} value={activeTabId || undefined} onChange={setActiveTabId} />
		</>
	);
};
